import React, { Suspense, useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, ContactShadows, Environment, Lightformer, useGLTF, Center } from '@react-three/drei';
import * as THREE from 'three';
import { X, RotateCcw, RefreshCw, Eye, Sparkles, Move3D } from 'lucide-react';
import cardGLB from '../assets/lanyard/card.glb';

interface CardFullscreenModalProps {
  isOpen: boolean;
  onClose: () => void;
  frontImage: string | null;
  backImage: string | null;
}

const FRONT_UV_RECT = { x: 0, y: 0, w: 0.5, h: 0.755 };
const BACK_UV_RECT = { x: 0.5, y: 0, w: 0.5, h: 0.757 };

interface InteractiveCardModelProps {
  frontImage: string | null;
  backImage: string | null;
  targetRotationY: number;
}

const InteractiveCardModel: React.FC<InteractiveCardModelProps> = ({
  frontImage,
  backImage,
  targetRotationY,
}) => {
  const meshRef = useRef<THREE.Group>(null);
  const { nodes, materials } = useGLTF(cardGLB) as any;

  // Composite front and back textures onto card material
  const cardTexture = React.useMemo(() => {
    const baseMap = materials.base.map;
    if (!frontImage && !backImage) return baseMap;

    const baseImg = baseMap.image;
    const W = baseImg?.width || 2048;
    const H = baseImg?.height || 2048;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return baseMap;

    if (baseImg) {
      ctx.drawImage(baseImg, 0, 0, W, H);
    }

    const drawFitted = (
      img: HTMLImageElement,
      rect: { x: number; y: number; w: number; h: number }
    ) => {
      const rx = rect.x * W;
      const ry = rect.y * H;
      const rw = rect.w * W;
      const rh = rect.h * H;
      const scale = Math.max(rw / img.width, rh / img.height);
      const dw = img.width * scale;
      const dh = img.height * scale;
      const dx = rx + (rw - dw) / 2;
      const dy = ry + (rh - dh) / 2;
      ctx.save();
      ctx.beginPath();
      ctx.rect(rx, ry, rw, rh);
      ctx.clip();
      ctx.drawImage(img, dx, dy, dw, dh);
      ctx.restore();
    };

    if (frontImage) {
      const fImg = new Image();
      fImg.src = frontImage;
      drawFitted(fImg, FRONT_UV_RECT);
    }

    if (backImage) {
      const bImg = new Image();
      bImg.src = backImage;
      drawFitted(bImg, BACK_UV_RECT);
    }

    const composite = new THREE.CanvasTexture(canvas);
    composite.colorSpace = THREE.SRGBColorSpace;
    composite.flipY = baseMap.flipY;
    composite.anisotropy = 16;
    composite.needsUpdate = true;
    return composite;
  }, [frontImage, backImage, materials.base.map]);

  // Smooth rotation animation when targetRotationY changes
  useFrame((_, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.y = THREE.MathUtils.damp(
        meshRef.current.rotation.y,
        targetRotationY,
        4,
        delta
      );
    }
  });

  return (
    <group ref={meshRef} scale={3.4}>
      <Center>
        <group>
          <mesh geometry={nodes.card.geometry}>
            <meshPhysicalMaterial
              map={cardTexture}
              map-anisotropy={16}
              clearcoat={1}
              clearcoatRoughness={0.1}
              roughness={0.25}
              metalness={0.65}
              reflectivity={0.9}
            />
          </mesh>
          <mesh
            geometry={nodes.clip.geometry}
            material={materials.metal}
            material-roughness={0.2}
            material-metalness={0.95}
          />
          <mesh
            geometry={nodes.clamp.geometry}
            material={materials.metal}
            material-roughness={0.2}
            material-metalness={0.95}
          />
        </group>
      </Center>
    </group>
  );
};

export const CardFullscreenModal: React.FC<CardFullscreenModalProps> = ({
  isOpen,
  onClose,
  frontImage,
  backImage,
}) => {
  const [rotationY, setRotationY] = useState(0);
  const [autoRotate, setAutoRotate] = useState(false);
  const controlsRef = useRef<any>(null);

  // Close on Escape key press, navigation events, and history changes
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) {
        onClose();
      }
    };

    const handleNavClose = () => {
      onClose();
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown, { passive: true });
      window.addEventListener('close-fullscreen-card', handleNavClose);
      window.addEventListener('popstate', handleNavClose);
      window.addEventListener('hashchange', handleNavClose);
    }

    return () => {
      document.body.style.overflow = 'auto';
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('close-fullscreen-card', handleNavClose);
      window.removeEventListener('popstate', handleNavClose);
      window.removeEventListener('hashchange', handleNavClose);
    };
  }, [isOpen, onClose]);

  const flipCard = () => {
    setRotationY((prev) => (prev === 0 ? Math.PI : 0));
  };

  const resetView = () => {
    setRotationY(0);
    setAutoRotate(false);
    if (controlsRef.current) {
      controlsRef.current.reset();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[9999] bg-slate-950/95 backdrop-blur-3xl flex items-center justify-center overflow-hidden select-none"
          onDoubleClick={onClose}
        >
          {/* Top Bar Navigation & Controls */}
          <div className="absolute top-3 sm:top-6 left-3 sm:left-8 right-3 sm:right-8 flex flex-wrap items-center justify-between gap-2.5 sm:gap-3 z-30 pointer-events-auto max-w-7xl mx-auto">
            {/* Title & Badge */}
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2.5 rounded-xl sm:rounded-2xl bg-brand-purple/20 border border-brand-purple/40 text-brand-purple dark:text-brand-light shadow-lg">
                <Move3D size={18} className="sm:w-5 sm:h-5" />
              </div>
              <div>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <h3 className="text-xs sm:text-base font-extrabold text-white tracking-tight">
                    VIP Engineer Pass
                  </h3>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold bg-brand-purple/30 text-brand-light border border-brand-purple/40">
                    <Sparkles size={10} />
                    <span>3D Model</span>
                  </span>
                </div>
                <p className="text-[10px] sm:text-[11px] text-slate-400 font-medium hidden md:block">
                  Drag to rotate 360° • Scroll wheel to zoom
                </p>
              </div>
            </div>

            {/* Actions: Flip, Auto-Spin, Reset, and Close */}
            <div className="flex items-center gap-1.5 sm:gap-2.5">
              <button
                type="button"
                onClick={flipCard}
                className="px-2.5 sm:px-4 py-1.5 sm:py-2.5 rounded-xl sm:rounded-2xl bg-brand-purple hover:bg-brand-purple/80 text-white font-semibold text-xs sm:text-sm flex items-center gap-1 sm:gap-2 transition-all shadow-md cursor-pointer"
              >
                <RefreshCw size={13} className="sm:w-3.5 sm:h-3.5" />
                <span>Flip ({rotationY === 0 ? 'Back' : 'Front'})</span>
              </button>

              <button
                type="button"
                onClick={() => setAutoRotate((prev) => !prev)}
                className={`px-2.5 sm:px-4 py-1.5 sm:py-2.5 rounded-xl sm:rounded-2xl font-semibold text-xs sm:text-sm flex items-center gap-1 sm:gap-2 transition-all border cursor-pointer ${
                  autoRotate
                    ? 'bg-brand-blue/30 border-brand-blue text-brand-light'
                    : 'bg-white/10 hover:bg-white/20 border-white/15 text-slate-200'
                }`}
              >
                <Eye size={13} className="sm:w-3.5 sm:h-3.5" />
                <span>{autoRotate ? 'Stop' : 'Spin'}</span>
              </button>

              <button
                type="button"
                onClick={resetView}
                className="p-1.5 sm:p-2.5 rounded-xl sm:rounded-2xl bg-white/10 hover:bg-white/20 text-slate-300 hover:text-white border border-white/15 transition-all cursor-pointer"
                title="Reset View"
                aria-label="Reset View"
              >
                <RotateCcw size={14} className="sm:w-3.5 sm:h-3.5" />
              </button>

              {/* Close Button */}
              <button
                type="button"
                onClick={onClose}
                aria-label="Close fullscreen 3D view"
                className="p-1.5 sm:p-2.5 rounded-xl sm:rounded-2xl bg-white/10 hover:bg-red-500/20 text-white hover:text-red-400 border border-white/20 hover:border-red-500/30 transition-all duration-200 cursor-pointer shadow-lg flex items-center gap-1 sm:gap-1.5"
              >
                <span className="text-xs font-semibold hidden sm:inline">Close</span>
                <X size={16} className="sm:w-4 sm:h-4" />
              </button>
            </div>
          </div>

          {/* Center 3D Interactive Canvas (100% Unobstructed) */}
          <div className="w-full h-full absolute inset-0 z-10 flex items-center justify-center cursor-grab active:cursor-grabbing">
            <Canvas
              camera={{ position: [0, 0, 9], fov: 30 }}
              dpr={[1, 2]}
              className="w-full h-full"
            >
              <ambientLight intensity={1.3} />
              <directionalLight position={[5, 8, 5]} intensity={2.5} castShadow />
              <directionalLight position={[-5, -4, -5]} intensity={1.2} color="#7C3AED" />
              <pointLight position={[0, 0, 4]} intensity={1.5} color="#C4B5FD" />

              <Suspense fallback={null}>
                <InteractiveCardModel
                  frontImage={frontImage}
                  backImage={backImage}
                  targetRotationY={rotationY}
                />
                <ContactShadows
                  position={[0, -2.6, 0]}
                  opacity={0.6}
                  scale={10}
                  blur={2}
                  far={6}
                  color="#7C3AED"
                />
                <Environment blur={0.6}>
                  <Lightformer
                    intensity={4}
                    color="#FFFFFF"
                    position={[0, 2, 4]}
                    scale={[10, 1, 1]}
                  />
                  <Lightformer
                    intensity={3}
                    color="#A78BFA"
                    position={[-3, 0, 2]}
                    scale={[5, 5, 1]}
                  />
                  <Lightformer
                    intensity={3}
                    color="#38BDF8"
                    position={[3, 0, 2]}
                    scale={[5, 5, 1]}
                  />
                </Environment>
              </Suspense>

              <OrbitControls
                ref={controlsRef}
                target={[0, 0, 0]}
                enablePan={false}
                enableZoom={true}
                minDistance={4}
                maxDistance={14}
                autoRotate={autoRotate}
                autoRotateSpeed={2}
                dampingFactor={0.06}
              />
            </Canvas>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CardFullscreenModal;

